import React from "react"
import PropTypes from "prop-types"
import flow from "lodash/flow"
import classNames from "classnames"
import Hidden from "@material-ui/core/Hidden/Hidden"
import Typography from "@material-ui/core/Typography/Typography"
import withStyles from "@material-ui/core/styles/withStyles"
import { translate } from "react-i18next"
import { Link } from "react-router-dom"

import styles from "./styles"

import LoginBg from "../LoginBg/LoginBg"
import TopNavigation from "../TopNavigation"
import DropDownMenu from "../DropDownMenu/DropDownMenu"
import { isCurrentCompanyAppical, currentCompany, constants, accessibilityURL } from "../../../constants"
import { getPrivacyUrl } from "../../../utils"

const createAdditionalItems = (t, privacyUrl) => [
    {
        id: 2,
        title: t("main:privacy"),
        action: () => {
            // eslint-disable-next-line no-undef
            window.open(privacyUrl, "_blank")
        },
    },
]

/**
 * Generates a Typography component with the specified properties.
 * 
 * @param {string} component - The HTML tag or React component to use as the root element.
 * @param {string} href - The URL to link to if the Typography component is rendered as an anchor element.
 * @param {string} text - The text content to display within the Typography component.
 * @param {string} align - The alignment of the text content within the Typography component.
 * @param {string} className - The CSS class name(s) to apply to the Typography component.
 * @returns {JSX.Element} The generated Typography component.
 */
export const generateTypography = (component, href, text, align, className) => (
    <Typography
        component={component}
        href={href}
        target={href ? "_blank" : null}
        rel={href ? "noopener noreferrer" : null}
        color="inherit"
        variant="caption"
        align={align}
        className={className}
    >
        {text}
    </Typography>
);

const LayoutLogin = ({
    t, children, classes, backgroundColor, backLink, backFunc,
}) => {
    const privacyUrl = getPrivacyUrl()
    const currentCompanyIsCapgeminiOrHeineken = currentCompany === "capgemini" || currentCompany === "heineken";


    const copyright = generateTypography(
        "span",
        null,
        t("main:copyright", { year: new Date().getFullYear() }),
        currentCompanyIsCapgeminiOrHeineken ? "left" : "right",
    );

    const privacyNoteMap = {
        capgemini: t("main:capgemini_privacy_note"),
        heineken: t("main:heineken_privacy_note"),
        default: t("main:privacy")
    };


    const privacyNote = privacyNoteMap[currentCompany] || privacyNoteMap.default;

    const privacy = generateTypography(
        "a",
        privacyUrl,
        privacyNote,
        currentCompanyIsCapgeminiOrHeineken ? "right" : "left",
        classes.simpleLink,
    );

    const accessibility = generateTypography(
        "a",
        accessibilityURL,
        t("main:accessibility"),
        currentCompanyIsCapgeminiOrHeineken ? "right" : "left",
        classes.simpleLink,
    );

    return (
        <div
            className={classNames(classes.mainContainer, {
                [classes.mainContainerPrimary]: backgroundColor === "primary",
                [classes.mainContainerResetPassword]: backgroundColor === "resetPassword",
            })}
        >
            <LoginBg color={backgroundColor} currentCompany={currentCompany} />
            <div className={classes.container}>

                <Hidden smUp>
                    {(currentCompany !== "capgemini" || currentCompany !== "heineken")
                        && (
                            <TopNavigation
                                backLink={backLink}
                                backFunc={backFunc}
                                transparent
                                rightElement={(
                                    <DropDownMenu
                                        id="menu-top"
                                        items={createAdditionalItems(t, privacyUrl)}
                                    />
                                )}
                            />
                        )}
                </Hidden>

                <div className={classes.flex} />

                {children}

                <div className={classes.flex} />

                {/* footer */}
                <div className={classes.loginBottom}>
                    <div className={classes.linkWidth}>
                        {currentCompanyIsCapgeminiOrHeineken ? copyright : privacy}
                    </div>


                    <div className={classes.accessibility}>
                        {accessibility}
                    </div>

                    <div className={classes.requestDataDeletion}>
                        <Link className={classes.simpleLink} underline="always" to={constants.userDataDeletionUrl} aria-label={t("main:requestDataDeletion")} color="inherit" onClick={backFunc}>
                            <Typography
                                component="span"
                                color="inherit"
                                variant="caption"
                            >
                                {t("main:request-data-deletion")}
                            </Typography>
                        </Link>
                    </div>


                    {(currentCompany !== "kpmg" && !isCurrentCompanyAppical) && (
                        <div className={classes.createAccountButtonWrapper}>
                            <Link to="/invite" className={classes.createAccountButton}>
                                <Typography color="inherit" variant="caption">
                                    {t("main:sign_up_here_link")}
                                </Typography>
                            </Link>
                        </div>
                    )}

                    <div className={classNames([classes.linkWidth, classes.flex, classes.paddingLeft16])}>
                        {currentCompanyIsCapgeminiOrHeineken ? privacy : copyright}
                    </div>
                </div>
                {/* End footer */}
            </div>
        </div>
    )
}

LayoutLogin.propTypes = {
    t: PropTypes.func.isRequired,
    children: PropTypes.node,
    classes: PropTypes.object.isRequired,
    backgroundColor: PropTypes.oneOf(["primary", "resetPassword"]),
    backLink: PropTypes.string,
    backFunc: PropTypes.func,
}

LayoutLogin.defaultProps = {
    children: null,
    backgroundColor: "primary",
    backLink: null,
    backFunc: null,
}

export default flow([
    withStyles(styles),
    translate(),
])(LayoutLogin)
